import * as React from "react"
import Row from "react-bootstrap/Row"
import Col from "react-bootstrap/Col"
import Spinner from "react-bootstrap/Spinner"

import AppContext from "./AppContext"

interface PendingRenderProps<D, E> {
  /**
   * Render the data return from `getData`
   * @param data  The data returned from `getData`
   */
  renderSuccess: (data: D, refresh?: () => void) => React.ReactNode

  /**
   * Render the error returned from `getData`
   * @param error  The error returned from `getData`
   */
  renderError?: (error: E) => React.ReactNode

  /**
   * Render the pending state before the `getData` promise is resolved
   * Default is a spinner
   */
  renderPending?: () => React.ReactNode

  renderPendingOnRefresh?: boolean
}

export interface PendingProps<D, E = any> extends PendingRenderProps<D, E> {
  /**
   * Request the data to be displayed
   */
  getData: () => Promise<D>

  /**
   * Optional callback when `getData` promise is successfully resolved
   */
  onSuccess?: (data: D) => void
}

interface PendingState<D, E> {
  state: "Pending" | "Error" | "Success"
  data?: D
  error?: E
}

/**
 * Handles rendering of `pending` and `error` states of a request so that view components
 * only have to care about rendering the data once it is available!
 *
 * @typeparam D  type of data to render on a successful request
 * @typeparam E  type of error to render on an unsuccessful request
 */
class Pending<D, E = any> extends React.Component<
  PendingProps<D, E>,
  PendingState<D, E>
> {
  public static contextType = AppContext
  protected retryTimer: any

  constructor(props: PendingProps<D, E>) {
    super(props)
    this.retryTimer = 0
    this.state = {
      state: "Pending"
    }
    this.getData()
  }

  public componentWillUnmount = () => {
    if (this.retryTimer) {
      clearTimeout(this.retryTimer)
      this.retryTimer = 0
    }
  }

  public render() {
    return PendingControlled(
      {
        ...this.props,
        ...this.state
      },
      this.refresh
    )
  }

  public refresh = () => {
    this.setState(
      {
        state: "Pending"
      },
      this.getData
    )
  }

  private setError(error: E) {
    this.setState(
      {
        state: "Error",
        error
      },
      () => {
        this.context.useToasts.addToast(error, "danger", true)
        this.retryTimer = setTimeout(this.refresh, 60000)
      }
    )
  }

  private setSuccess(data: D) {
    this.setState(
      {
        state: "Success",
        data
      },
      () => {
        if (this.props.onSuccess) {
          this.props.onSuccess(data)
        }
      }
    )
  }

  private getData = async () => {
    try {
      const data = await this.props.getData()
      this.setSuccess(data)
    } catch (error) {
      this.setError(error as any)
    }
  }
}

export interface PendingControlledProps<D, E>
  extends PendingRenderProps<D, E>,
    PendingState<D, E> {}

/**
 * Controlled version of the pending component. State is kept by parent component and passed
 * as props to `PendingControlled`
 */
export function PendingControlled<D, E>(
  props: PendingControlledProps<D, E>,
  refresh?: () => void
) {
  switch (props.state) {
    case "Pending": {
      if (props.data && !props.renderPendingOnRefresh) {
        return props.renderSuccess(props.data, refresh) // Keep rendering old data
      } else {
        return props.renderPending ? (
          props.renderPending()
        ) : (
          <PendingSpinnerCentered />
        )
      }
    }
    case "Success": {
      return props.renderSuccess(props.data!)
    }
    default:
    case "Error":
      return props.renderError ? (
        props.renderError(props.error!)
      ) : (
        <Row className="d-flex flex-fill justify-content-center">
          <Col sm={12} md={8} xl={5} className="my-auto text-center">
            <p className="display-4">Oops!</p>
            <p className="mb-4 text-muted">
              It seems like an we've experienced an issue while fetching your
              data. We'll try again after a minute.
            </p>
          </Col>
        </Row>
      )
  }
}

export const PendingSpinnerCentered = () => (
  <Row className="d-flex flex-fill justify-content-center">
    <Col sm={12} md={8} xl={5} className="my-auto text-center">
      <Spinner animation="border" variant="primary" />
    </Col>
  </Row>
)

export default Pending
